import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, Clock, Eye, AlertTriangle, User, X,
  FileText, Building, Shield, Search, Filter, TrendingUp,
  DollarSign, CreditCard, Calendar, MapPin, Mail,
  Hash, Package, History, AlertCircle, ExternalLink
} from 'lucide-react';
import { useToast } from './ui/Toast';
import { getBackendBaseUrl } from '../utils/api';
import './KYCManagement.css';

const KYCManagement = () => {
  const toast = useToast();
  const [kycList, setKycList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [flyoutData, setFlyoutData] = useState(null);
  const [filterStatus, setFilterStatus] = useState('PENDING_REVIEW');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const fetchKYCList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/kyc/admin/pending?status=${filterStatus}&page=${currentPage}&limit=20`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setKycList(data.kycRecords);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch KYC list:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, currentPage]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch('/api/kyc/admin/stats', { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch KYC stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchKYCList();
    fetchStats();
  }, [fetchKYCList, fetchStats]);

  const viewKycDetails = async (kycId) => {
    try {
      setSelectedKyc(kycId);
      const response = await fetch(`/api/kyc/admin/${kycId}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setFlyoutData(data);
      }
    } catch (error) {
      console.error('Failed to fetch KYC details:', error);
    }
  };

  const closeFlyout = () => {
    setSelectedKyc(null);
    setFlyoutData(null);
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(`/api/kyc/admin/${selectedKyc}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ adminNotes: '' })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('KYC approved successfully');
        setShowApproveModal(false);
        closeFlyout();
        fetchKYCList();
        fetchStats();
      } else {
        toast.error(data.error || 'Failed to approve KYC');
      }
    } catch (error) {
      console.error('Failed to approve KYC:', error);
      toast.error('Error approving KYC');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`/api/kyc/admin/${selectedKyc}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rejectionReason: rejectReason, adminNotes: '' })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('KYC rejected. Merchant will be notified.');
        setShowRejectModal(false);
        setRejectReason('');
        closeFlyout();
        fetchKYCList();
        fetchStats();
      } else {
        toast.error(data.error || 'Failed to reject KYC');
      }
    } catch (error) {
      console.error('Failed to reject KYC:', error);
      toast.error('Error rejecting KYC');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      NOT_STARTED: { color: '#6B7280', icon: Clock, text: 'Not Started' },
      IN_PROGRESS: { color: '#1a6f63', icon: Clock, text: 'In Progress' },
      PENDING_REVIEW: { color: '#D97706', icon: AlertTriangle, text: 'Pending Review' },
      UNDER_REVIEW: { color: '#D97706', icon: Eye, text: 'Under Review' },
      APPROVED: { color: '#059669', icon: CheckCircle, text: 'Approved' },
      REJECTED: { color: '#DC2626', icon: XCircle, text: 'Rejected' },
      RESUBMISSION_REQUIRED: { color: '#DC2626', icon: AlertTriangle, text: 'Resubmission Required' }
    };
    const badge = badges[status] || badges.NOT_STARTED;
    const Icon = badge.icon;
    return (
      <span className="kyc-status-badge" style={{ backgroundColor: `${badge.color}15`, color: badge.color }}>
        <Icon size={14} />
        {badge.text}
      </span>
    );
  };

  const getLevelBadge = (level) => {
    const levels = {
      LEVEL_0: { color: '#6B7280', text: 'Level 0' },
      LEVEL_1: { color: '#1a6f63', text: 'Level 1' },
      LEVEL_2: { color: '#059669', text: 'Level 2' },
      LEVEL_3: { color: '#8B5CF6', text: 'Level 3' }
    };
    const levelInfo = levels[level] || levels.LEVEL_0;
    return (
      <span className="kyc-level-badge" style={{ backgroundColor: `${levelInfo.color}15`, color: levelInfo.color }}>
        {levelInfo.text}
      </span>
    );
  };

  const filteredList = kycList.filter(kyc =>
    kyc.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kyc.businessName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = pagination?.totalPages || pagination?.pages || 1;
  const totalRecords = pagination?.total || filteredList.length;
  const pageLimit = pagination?.limit || 20;
  const pageStart = totalRecords > 0 ? ((currentPage - 1) * pageLimit) + 1 : 0;
  const pageEnd = Math.min(currentPage * pageLimit, totalRecords);

  return (
    <div className="kyc-management-container">
      <div className="kyc-page-head">
        <div className="kyc-title-block">
          <span className="kyc-eyebrow">Security review</span>
          <h1>KYC Management</h1>
          <p>Prioritize merchant verification, inspect evidence, and close reviews cleanly.</p>
        </div>
        {stats && (
          <div className="kyc-priority-chip">
            <Clock size={16} />
            <span>{stats.byStatus.pendingReview || 0} waiting</span>
          </div>
        )}
      </div>

      {stats && (
        <div className="kyc-stats-grid" aria-label="KYC review summary">
          <div className="kyc-stat-card pending">
            <span className="stat-icon"><Clock size={18} /></span>
            <span className="stat-label">Pending</span>
            <strong className="stat-value">{stats.byStatus.pendingReview || 0}</strong>
          </div>
          <div className="kyc-stat-card approved">
            <span className="stat-icon"><CheckCircle size={18} /></span>
            <span className="stat-label">Approved</span>
            <strong className="stat-value">{stats.byStatus.approved || 0}</strong>
          </div>
          <div className="kyc-stat-card rejected">
            <span className="stat-icon"><XCircle size={18} /></span>
            <span className="stat-label">Rejected</span>
            <strong className="stat-value">{stats.byStatus.rejected || 0}</strong>
          </div>
          <div className="kyc-stat-card total">
            <span className="stat-icon"><User size={18} /></span>
            <span className="stat-label">Total</span>
            <strong className="stat-value">{stats.total || 0}</strong>
          </div>
        </div>
      )}

      <div className="kyc-controls">
        <label className="kyc-search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search email or business"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </label>
        <label className="kyc-filter">
          <Filter size={16} />
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
            <option value="all">All statuses</option>
            <option value="PENDING_REVIEW">Pending review</option>
            <option value="UNDER_REVIEW">Under review</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="IN_PROGRESS">In progress</option>
          </select>
        </label>
      </div>

      {/* KYC Table */}
      <div className="kyc-table-wrapper">
        {loading ? (
          <div className="kyc-loading">
            <div className="spinner"></div>
            <p>Loading KYC submissions...</p>
          </div>
        ) : filteredList.length === 0 ? (
          <div className="kyc-empty-state">
            <AlertCircle size={48} />
            <h3>No KYC submissions found</h3>
            <p>Try adjusting your filters or search term</p>
          </div>
        ) : (
          <table className="kyc-table" aria-label="KYC submissions">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Business</th>
                <th>Level</th>
                <th>Status</th>
                <th>Agreement</th>
                <th>Submitted</th>
                <th>Progress</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((kyc) => (
                <tr key={kyc.id} onClick={() => viewKycDetails(kyc.id)}>
                  <td data-label="Merchant">
                    <div className="merchant-cell">
                      <div className="merchant-avatar">
                        {kyc.user.email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="merchant-email">{kyc.user.email}</div>
                        <div className="merchant-id">ID: {kyc.user.id.substring(0, 8)}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Business">
                    <div className="business-cell">
                      {kyc.businessName || '-'}
                      {kyc.isRegisteredBusiness && (
                        <Building size={14} className="business-icon" title="Registered Business" />
                      )}
                    </div>
                  </td>
                  <td data-label="Level">{getLevelBadge(kyc.level)}</td>
                  <td data-label="Status">{getStatusBadge(kyc.status)}</td>
                  <td data-label="Agreement">
                    {kyc.settlementAgreementAccepted ? (
                      <span className="agreement-badge signed" title={`Signed ${kyc.settlementAgreementAcceptedAt ? new Date(kyc.settlementAgreementAcceptedAt).toLocaleDateString() : ''}`}>
                        <CheckCircle size={14} /> Signed
                      </span>
                    ) : (
                      <span className="agreement-badge not-signed">
                        <XCircle size={14} /> Not Signed
                      </span>
                    )}
                  </td>
                  <td data-label="Submitted">
                    {kyc.submittedAt
                      ? new Date(kyc.submittedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : '-'}
                  </td>
                  <td data-label="Progress">
                    <div className="progress-cell">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${kyc.progressPercentage}%` }}
                        />
                      </div>
                      <span className="progress-text">{kyc.progressPercentage}%</span>
                    </div>
                  </td>
                  <td data-label="Action">
                    <button className="view-btn" onClick={(e) => { e.stopPropagation(); viewKycDetails(kyc.id); }}>
                      <Eye size={16} />
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="kyc-pagination">
          <span>
            Showing {pageStart}-{pageEnd} of {totalRecords}
          </span>
          {totalPages > 1 && (
            <div className="kyc-pagination-controls">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <strong>Page {currentPage} of {totalPages}</strong>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Flyout */}
      {selectedKyc && flyoutData && (
        <KYCReviewFlyout
          data={flyoutData}
          onClose={closeFlyout}
          onApprove={() => setShowApproveModal(true)}
          onReject={() => setShowRejectModal(true)}
          actionLoading={actionLoading}
        />
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="kyc-modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="kyc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kyc-modal-header">
              <div className="kyc-modal-icon approve">
                <CheckCircle size={24} />
              </div>
              <h3>Approve KYC Submission</h3>
              <p>Are you sure you want to approve this KYC verification?</p>
            </div>
            <div className="kyc-modal-body">
              <p className="kyc-modal-info">
                The merchant will be notified and their verification level will be updated.
                They will gain access to additional platform features.
              </p>
            </div>
            <div className="kyc-modal-actions">
              <button
                className="kyc-modal-btn cancel"
                onClick={() => setShowApproveModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="kyc-modal-btn approve"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {actionLoading ? 'Approving...' : 'Approve KYC'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="kyc-modal-overlay" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>
          <div className="kyc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kyc-modal-header">
              <div className="kyc-modal-icon reject">
                <XCircle size={24} />
              </div>
              <h3>Reject KYC Submission</h3>
              <p>Please provide a reason for rejection</p>
            </div>
            <div className="kyc-modal-body">
              <label className="kyc-modal-label">Rejection Reason</label>
              <textarea
                className="kyc-modal-textarea"
                placeholder="Enter the reason for rejecting this KYC submission..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
              />
              <p className="kyc-modal-hint">
                This reason will be sent to the merchant so they can address the issues.
              </p>
            </div>
            <div className="kyc-modal-actions">
              <button
                className="kyc-modal-btn cancel"
                onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="kyc-modal-btn reject"
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
              >
                {actionLoading ? 'Rejecting...' : 'Reject KYC'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Flyout Component
const KYCReviewFlyout = ({ data, onClose, onApprove, onReject, actionLoading }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const { kyc, documents, logs, bankAccountHistory, merchantDetails } = data;
  const [selectedDocumentId, setSelectedDocumentId] = useState(documents?.[0]?.id || null);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const formatLabel = (value) => {
    if (!value) return 'N/A';
    return String(value)
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getDocumentUrl = (doc) => {
    if (!doc?.documentUrl) return '';
    return doc.documentUrl.startsWith('http') ? doc.documentUrl : `${getBackendBaseUrl()}${doc.documentUrl}`;
  };

  const getDocumentPreviewType = (doc) => {
    const mimeType = doc?.mimeType?.toLowerCase() || '';
    const url = getDocumentUrl(doc).split('?')[0].toLowerCase();
    if (mimeType.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(url)) return 'image';
    if (mimeType === 'application/pdf' || url.endsWith('.pdf')) return 'pdf';
    return 'unsupported';
  };

  const formatFileSize = (size) => {
    if (!size) return 'N/A';
    return `${(size / 1024).toFixed(1)} KB`;
  };

  const documentCount = documents?.length || 0;
  const failedChecks = logs?.filter((log) => log.success === false).length || 0;
  const canAct = kyc.status === 'PENDING_REVIEW' || kyc.status === 'RESUBMISSION_REQUIRED' || kyc.status === 'IN_PROGRESS';
  const selectedDocument = documents?.find((doc) => doc.id === selectedDocumentId) || documents?.[0];
  const selectedDocumentUrl = getDocumentUrl(selectedDocument);
  const selectedPreviewType = getDocumentPreviewType(selectedDocument);

  useEffect(() => {
    if (!documents?.length) return;
    if (!documents.some((doc) => doc.id === selectedDocumentId)) {
      setSelectedDocumentId(documents[0].id);
    }
  }, [documents, selectedDocumentId]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'business', label: 'Business', icon: Building },
    { id: 'identity', label: 'Identity', icon: Shield },
    { id: 'banking', label: 'Banking', icon: CreditCard },
    { id: 'documents', label: `Docs ${documentCount}`, icon: FileText },
    { id: 'history', label: 'History', icon: History }
  ];

  const reviewChecks = [
    {
      label: 'Settlement agreement',
      value: kyc.settlementAgreementAccepted ? 'Signed' : 'Missing',
      passed: !!kyc.settlementAgreementAccepted
    },
    {
      label: 'Bank account',
      value: kyc.accountVerified ? 'Verified' : 'Not verified',
      passed: !!kyc.accountVerified
    },
    {
      label: 'NIN',
      value: kyc.ninVerified ? 'Verified' : (kyc.nin ? 'Provided' : 'Missing'),
      passed: !!kyc.ninVerified
    },
    {
      label: 'BVN',
      value: kyc.bvnVerified ? 'Verified' : (kyc.bvn ? 'Provided' : 'Missing'),
      passed: !!kyc.bvnVerified
    }
  ];

  return (
    <>
      <div className="flyout-overlay" onClick={onClose} aria-hidden="true" />
      <aside className="kyc-flyout" role="dialog" aria-modal="true" aria-labelledby="kyc-review-title">
        <div className="flyout-header">
          <div className="flyout-identity">
            <span className="kyc-eyebrow">Review file</span>
            <h2 id="kyc-review-title">{kyc.businessName || kyc.user.email}</h2>
            <p>{kyc.user.email}</p>
            <div className="flyout-badges">
              <span className={`status-pill status-${kyc.status.toLowerCase()}`}>{formatLabel(kyc.status)}</span>
              <span className="level-pill">{formatLabel(kyc.level)}</span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Close KYC review">
            <X size={20} />
          </button>
        </div>

        <div className="flyout-summary" aria-label="Review summary">
          <div>
            <span>Status</span>
            <strong>{formatLabel(kyc.status)}</strong>
          </div>
          <div>
            <span>Documents</span>
            <strong>{documentCount}</strong>
          </div>
          <div>
            <span>Failed checks</span>
            <strong>{failedChecks}</strong>
          </div>
          <div>
            <span>Submitted</span>
            <strong>{kyc.submittedAt ? formatDate(kyc.submittedAt) : 'N/A'}</strong>
          </div>
        </div>

        <div className="flyout-tabs" role="tablist" aria-label="KYC review sections">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flyout-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="info-section">
                <div className="section-heading">
                  <h3>Review Readiness</h3>
                  <span>{reviewChecks.filter((check) => check.passed).length} of {reviewChecks.length} verified</span>
                </div>
                <div className="review-check-grid">
                  {reviewChecks.map((check) => (
                    <div key={check.label} className={`review-check ${check.passed ? 'passed' : 'attention'}`}>
                      {check.passed ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                      <div>
                        <label>{check.label}</label>
                        <strong>{check.value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="info-section">
                <div className="section-heading">
                  <h3>Merchant</h3>
                </div>
                <div className="info-grid">
                  <div className="info-item">
                    <Mail size={16} />
                    <div>
                      <label>Email</label>
                      <span>{kyc.user.email}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <User size={16} />
                    <div>
                      <label>Username</label>
                      <span>{kyc.user.username || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <Calendar size={16} />
                    <div>
                      <label>Member Since</label>
                      <span>{formatDate(kyc.user.createdAt)}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <Hash size={16} />
                    <div>
                      <label>Merchant Tier</label>
                      <span>{kyc.user.merchantTier || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <MapPin size={16} />
                    <div>
                      <label>Country</label>
                      <span>{kyc.user.country || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <DollarSign size={16} />
                    <div>
                      <label>Currency</label>
                      <span>{kyc.user.currency || 'NGN'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {merchantDetails.store && (
                <div className="info-section">
                  <div className="section-heading">
                    <h3>Store</h3>
                  </div>
                  <div className="info-grid">
                    <div className="info-item">
                      <Package size={16} />
                      <div>
                        <label>Store Name</label>
                        <span>{merchantDetails.store.name}</span>
                      </div>
                    </div>
                    <div className="info-item">
                      <Hash size={16} />
                      <div>
                        <label>Store Slug</label>
                        <span>{merchantDetails.store.slug}</span>
                      </div>
                    </div>
                    <div className="info-item">
                      <Calendar size={16} />
                      <div>
                        <label>Created</label>
                        <span>{formatDate(merchantDetails.store.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="info-section">
                <div className="section-heading">
                  <h3>Commercial Signals</h3>
                </div>
                <div className="kyc-metrics-grid">
                  <div className="kyc-metric-card">
                    <TrendingUp size={20} />
                    <div>
                      <div className="kyc-metric-value">{merchantDetails.orderCount}</div>
                      <div className="kyc-metric-label">Completed Orders</div>
                    </div>
                  </div>
                  <div className="kyc-metric-card">
                    <DollarSign size={20} />
                    <div>
                      <div className="kyc-metric-value">{formatCurrency(merchantDetails.totalRevenue)}</div>
                      <div className="kyc-metric-label">Total Revenue</div>
                    </div>
                  </div>
                  <div className="kyc-metric-card">
                    <Clock size={20} />
                    <div>
                      <div className="kyc-metric-value">{formatCurrency(merchantDetails.pendingSettlements)}</div>
                      <div className="kyc-metric-label">Pending Settlements</div>
                    </div>
                  </div>
                  <div className="kyc-metric-card">
                    <CreditCard size={20} />
                    <div>
                      <div className="kyc-metric-value">{merchantDetails.enabledGateways.length}</div>
                      <div className="kyc-metric-label">Payment Gateways</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <div className="section-heading">
                  <h3>Limits & Access</h3>
                </div>
                <div className="kyc-status-grid">
                  <div className="kyc-status-item">
                    <label>Verification Level</label>
                    <span className="level-badge">{formatLabel(kyc.level)}</span>
                  </div>
                  <div className="kyc-status-item">
                    <label>Current Status</label>
                    <span className={`status-${kyc.status.toLowerCase()}`}>{formatLabel(kyc.status)}</span>
                  </div>
                  <div className="kyc-status-item">
                    <label>Can Receive Payments</label>
                    <span className={kyc.canReceivePayments ? 'yes' : 'no'}>
                      {kyc.canReceivePayments ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="kyc-status-item">
                    <label>Can Withdraw</label>
                    <span className={kyc.canWithdrawFunds ? 'yes' : 'no'}>
                      {kyc.canWithdrawFunds ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="kyc-status-item">
                    <label>Daily Limit</label>
                    <span>{kyc.dailyTransactionLimit ? formatCurrency(kyc.dailyTransactionLimit) : 'Unlimited'}</span>
                  </div>
                  <div className="kyc-status-item">
                    <label>Monthly Limit</label>
                    <span>{kyc.monthlyTransactionLimit ? formatCurrency(kyc.monthlyTransactionLimit) : 'Unlimited'}</span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <div className="section-heading">
                  <h3>Settlement Agreement</h3>
                </div>
                <div className="kyc-status-grid">
                  <div className="kyc-status-item">
                    <label>Agreement Accepted</label>
                    <span className={kyc.settlementAgreementAccepted ? 'yes' : 'no'}>
                      {kyc.settlementAgreementAccepted ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {kyc.settlementAgreementAccepted && (
                    <>
                      <div className="kyc-status-item">
                        <label>Version</label>
                        <span>{kyc.settlementAgreementVersion || 'N/A'}</span>
                      </div>
                      <div className="kyc-status-item">
                        <label>Accepted At</label>
                        <span>{kyc.settlementAgreementAcceptedAt ? new Date(kyc.settlementAgreementAcceptedAt).toLocaleString() : 'N/A'}</span>
                      </div>
                      <div className="kyc-status-item">
                        <label>IP Address</label>
                        <span className="mono-text">{kyc.settlementAgreementIp || 'N/A'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'business' && (
            <div className="business-tab">
              <div className="info-section">
                <div className="section-heading">
                  <h3>Business Information</h3>
                </div>
                <div className="detail-list">
                  <div className="detail-row">
                    <label>Business Name</label>
                    <span>{kyc.businessName || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Business Type</label>
                    <span>{kyc.businessType || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Registered Business</label>
                    <span>{kyc.isRegisteredBusiness ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Trading As</label>
                    <span>{kyc.tradingAs || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Business Address</label>
                    <span>{kyc.businessAddress || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>City</label>
                    <span>{kyc.city || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>State</label>
                    <span>{kyc.state || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Postal Code</label>
                    <span>{kyc.postalCode || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'identity' && (
            <div className="identity-tab">
              <div className="info-section">
                <div className="section-heading">
                  <h3>Identity Information</h3>
                </div>
                <div className="detail-list">
                  <div className="detail-row">
                    <label>ID Type</label>
                    <span>{kyc.idType || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>ID Number</label>
                    <span>{kyc.idNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>NIN</label>
                    <span>
                      {kyc.nin || 'N/A'}
                      {kyc.ninVerified && <CheckCircle size={16} color="#10B981" style={{ marginLeft: '8px' }} />}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>BVN</label>
                    <span>
                      {kyc.bvn || 'N/A'}
                      {kyc.bvnVerified && <CheckCircle size={16} color="#10B981" style={{ marginLeft: '8px' }} />}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'banking' && (
            <div className="banking-tab">
              <div className="info-section">
                <div className="section-heading">
                  <h3>Current Bank Account</h3>
                </div>
                <div className="detail-list">
                  <div className="detail-row">
                    <label>Bank Name</label>
                    <span>{kyc.bankName || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Account Number</label>
                    <span>{kyc.accountNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Account Name</label>
                    <span>
                      {kyc.accountName || 'N/A'}
                      {kyc.accountVerified && <CheckCircle size={16} color="#10B981" style={{ marginLeft: '8px' }} />}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>Bank Code</label>
                    <span>{kyc.bankCode || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {bankAccountHistory && bankAccountHistory.length > 0 && (
                <div className="info-section">
                  <div className="section-heading">
                    <h3>Bank Account Change History</h3>
                  </div>
                  <div className="history-timeline">
                    {bankAccountHistory.map((history, index) => (
                      <div key={history.id} className="history-item">
                        <div className="history-dot" />
                        <div className="history-content">
                          <div className="history-header">
                            <span className="history-type">{history.changeType.replace('_', ' ')}</span>
                            <span className="history-date">{formatDate(history.createdAt)}</span>
                          </div>
                          <div className="history-details">
                            <p>{history.changeReason}</p>
                            {history.newAccountNumber && (
                              <div className="account-info">
                                <Building size={14} />
                                <span>{history.newBankName} - ****{history.newAccountNumber.slice(-4)}</span>
                              </div>
                            )}
                            {history.verificationSuccess !== null && (
                              <div className={`verification-badge ${history.verificationSuccess ? 'success' : 'failed'}`}>
                                {history.verificationSuccess ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                {history.verificationSuccess ? 'Verified' : 'Verification Failed'}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="documents-tab">
              {documents && documents.length > 0 ? (
                <div className="document-review-layout">
                  <div className="document-preview-panel">
                    <div className="document-preview-head">
                      <div>
                        <span>Preview</span>
                        <h3>{formatLabel(selectedDocument?.documentType)}</h3>
                      </div>
                      {selectedDocumentUrl && (
                        <a href={selectedDocumentUrl} target="_blank" rel="noopener noreferrer">
                          Open <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                    <div className="document-preview-frame">
                      {selectedPreviewType === 'image' && (
                        <img src={selectedDocumentUrl} alt={formatLabel(selectedDocument?.documentType)} />
                      )}
                      {selectedPreviewType === 'pdf' && (
                        <iframe title={formatLabel(selectedDocument?.documentType)} src={selectedDocumentUrl} />
                      )}
                      {selectedPreviewType === 'unsupported' && (
                        <div className="document-preview-empty">
                          <FileText size={38} />
                          <p>This file type cannot be previewed inline.</p>
                          {selectedDocumentUrl && (
                            <a href={selectedDocumentUrl} target="_blank" rel="noopener noreferrer">
                              Open original <ExternalLink size={14} />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="document-list" aria-label="Uploaded documents">
                    {documents.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        className={`document-card ${selectedDocument?.id === doc.id ? 'selected' : ''}`}
                        onClick={() => setSelectedDocumentId(doc.id)}
                      >
                        <div className="document-header">
                          <FileText size={18} />
                          <span className={`doc-status ${doc.status.toLowerCase()}`}>{formatLabel(doc.status)}</span>
                        </div>
                        <div className="document-body">
                          <h4>{formatLabel(doc.documentType)}</h4>
                          <div className="document-meta">
                            <span>{doc.fileName || 'Uploaded document'}</span>
                            <span>{formatFileSize(doc.fileSize)} · {formatDate(doc.createdAt)}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <FileText size={48} />
                  <p>No documents uploaded</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="history-tab">
              <div className="info-section">
                <div className="section-heading">
                  <h3>Verification Logs</h3>
                </div>
                {logs && logs.length > 0 ? (
                  <div className="logs-list">
                    {logs.map((log) => (
                      <div key={log.id} className="log-item">
                        <div className="log-header">
                          <span className="log-type">{log.verificationType}</span>
                          <span className={`log-status ${log.success ? 'success' : 'failed'}`}>
                            {log.success ? 'Success' : 'Failed'}
                          </span>
                        </div>
                        <div className="log-details">
                          <div className="log-meta">
                            <span>Provider: {log.provider}</span>
                            <span>{formatDate(log.createdAt)}</span>
                          </div>
                          {log.errorMessage && (
                            <div className="log-error">
                              <span>Error: {log.errorMessage}</span>
                            </div>
                          )}
                          {log.responseData && (
                            <div className="log-data">
                              <pre>{JSON.stringify(log.responseData, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <Shield size={48} />
                    <p>No verification logs</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {canAct && (
          <div className="flyout-actions">
            <button
              className="reject-btn"
              onClick={onReject}
              disabled={actionLoading}
            >
              <XCircle size={18} />
              Reject
            </button>
            <button
              className="approve-btn"
              onClick={onApprove}
              disabled={actionLoading}
            >
              <CheckCircle size={18} />
              Approve
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default KYCManagement;
